import { useEffect, useState, type RefObject } from 'react'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Modale } from '../../componenti/base.tsx'
import { MESSAGGIO_ALTEZZA, MESSAGGIO_COLLEGAMENTO } from '../../servizi/posta.ts'
import * as regole from '../../servizi/regole-collegamenti-messaggio.ts'

/**
 * Collegamenti nei messaggi (decisioni di Davide del 2/11/2026): si toccano nel testo del messaggio, come
 * in ogni programma di posta, ma non si aprono subito. Prima compare la finestra «Aprire il
 * collegamento?» con il dominio in grande, l'indirizzo intero e gli avvisi; si apre solo da li'. Lo scopo
 * e' che nessuno apra un sito di phishing per distrazione. Il riquadro «Collegamenti nel messaggio» sotto
 * il corpo, della prima versione, non c'e' piu'.
 *
 * - Corpo HTML: l'iframe isolato manda indirizzo e testo con postMessage (servizi/posta.ts,
 *   paginaMessaggio); si accettano solo i messaggi di quell'iframe e solo indirizzi http e https.
 * - Testo semplice: gli indirizzi diventano comandi nel testo (TestoConCollegamenti).
 * Le regole stanno in servizi/regole-collegamenti-messaggio.ts. Vale per la webmail e per l'archivio delle
 * caselle.
 */

/** Il collegamento toccato nel corpo HTML di quell'iframe, pronto per la finestra; null se non c'e'. */
export function useCollegamentoToccato(iframe: RefObject<HTMLIFrameElement | null>) {
  const [scelto, setScelto] = useState<regole.Verifica | null>(null)
  useEffect(() => {
    function ricevi(e: MessageEvent) {
      if (!iframe.current || e.source !== iframe.current.contentWindow) return
      const d = e.data as { tipo?: unknown; href?: unknown; testo?: unknown } | null
      if (!d || d.tipo !== MESSAGGIO_COLLEGAMENTO || typeof d.href !== 'string') return
      const indirizzo = regole.indirizzoWeb(d.href)
      // mailto:, tel:, ancore interne: niente da aprire fuori dal gestionale.
      if (!indirizzo) return
      const testo = typeof d.testo === 'string' ? d.testo.slice(0, 200) : ''
      setScelto(regole.verificaCollegamento({ url: indirizzo.href, testi: testo ? [testo] : [] }))
    }
    window.addEventListener('message', ricevi)
    return () => window.removeEventListener('message', ricevi)
  }, [iframe])
  return { scelto, chiudi: () => setScelto(null) }
}

/**
 * L'iframe del corpo alto quanto il messaggio (2/11/2026, «la webmail è troppo piccola»): senza altezza il
 * browser gli dava 150 px e il testo scorreva in una feritoia. Lo script del corpo manda l'altezza del
 * documento a ogni cambio (immagini caricate, larghezza diversa); si accetta solo un numero, e solo da
 * quell'iframe. Il tetto evita che un messaggio costruito apposta allunghi la pagina senza fine.
 */
export function useAltezzaCornice(iframe: RefObject<HTMLIFrameElement | null>) {
  useEffect(() => {
    function ricevi(e: MessageEvent) {
      const cornice = iframe.current
      if (!cornice || e.source !== cornice.contentWindow) return
      const d = e.data as { tipo?: unknown; altezza?: unknown } | null
      if (!d || d.tipo !== MESSAGGIO_ALTEZZA || typeof d.altezza !== 'number' || !Number.isFinite(d.altezza)) return
      cornice.style.height = `${Math.min(Math.max(Math.ceil(d.altezza) + 2, 80), 40_000)}px`
    }
    window.addEventListener('message', ricevi)
    return () => window.removeEventListener('message', ricevi)
  }, [iframe])
}

/** Testo semplice con gli indirizzi web come comandi nel testo, che aprono la finestra di conferma. */
export function TestoConCollegamenti({ testo }: { testo: string }) {
  const [scelto, setScelto] = useState<regole.Verifica | null>(null)
  return (
    <>
      <p className="posta-testo">
        {regole.pezziDelTesto(testo).map((p, i) => {
          if ('testo' in p) return <span key={i}>{p.testo}</span>
          const indirizzo = regole.indirizzoWeb(p.indirizzo)
          if (!indirizzo) return <span key={i}>{p.indirizzo}</span>
          return (
            <button key={i} type="button" className="posta-collegamento-in-linea"
                    onClick={() => setScelto(regole.verificaCollegamento({ url: indirizzo.href, testi: [] }))}>
              {p.indirizzo}
            </button>
          )
        })}
      </p>
      {scelto && <ConfermaApertura verifica={scelto} chiudi={() => setScelto(null)} />}
    </>
  )
}

/**
 * La finestra prima dell'apertura. «Annulla» e' il comando pieno e prende il fuoco: con Invio o un tocco
 * distratto non si apre niente. Il sito si apre in una scheda nuova, senza opener e senza referrer.
 */
export function ConfermaApertura({ verifica: v, chiudi }: { verifica: regole.Verifica; chiudi: () => void }) {
  function apri() {
    window.open(v.url, '_blank', 'noopener,noreferrer')
    chiudi()
  }

  return (
    <Modale titolo="Aprire il collegamento?" chiudi={chiudi} fuocoIniziale="azioni"
      azioni={
        <>
          <Bottone variante="primario" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante={v.grave ? 'critico' : 'secondario'} onClick={apri}>Apri il collegamento</Bottone>
        </>
      }>
      {v.controlli.filter((k) => k.tipo === 'testo-diverso').map((k, i) => <Avvertimento key={i} controllo={k} />)}
      <div className="collegamento-destinazione">
        <span className="piccolo tenue">Il collegamento porta a</span>
        <strong className="collegamento-dominio">{v.dominio}</strong>
      </div>
      <p>
        Controlla che il dominio sia quello che ti aspetti. Se non ne sei sicuro, non aprire il collegamento e
        non inserire password o dati di pagamento.
      </p>
      {v.controlli.filter((k) => k.tipo !== 'testo-diverso').map((k, i) => <Avvertimento key={i} controllo={k} />)}
      <div className="collegamento-indirizzo">
        <span className="piccolo tenue">Indirizzo completo</span>
        <p className="collegamento-url">{v.url}</p>
      </div>
    </Modale>
  )
}

/** Un controllo detto a parole: rosso il testo ingannevole, gli altri da guardare con attenzione. */
function Avvertimento({ controllo: k }: { controllo: regole.Controllo }) {
  switch (k.tipo) {
    case 'testo-diverso':
      return (
        <Avviso tipo="errore">
          Il testo del collegamento mostra «{k.testo}», ma il collegamento porta a <strong>{k.dominio}</strong>, non
          a {k.dominioTesto}. È il trucco più usato nelle truffe via email: non aprirlo se non sei sicuro di chi te
          lo manda.
        </Avviso>
      )
    case 'credenziali':
      return (
        <Avviso tipo="attenzione">
          Prima del dominio c’è «{k.prima}@»: serve a far sembrare l’indirizzo quello di un altro sito. Il
          collegamento porta a <strong>{k.dominio}</strong>.
        </Avviso>
      )
    case 'sottodominio':
      return (
        <Avviso tipo="attenzione">
          Il nome comincia con «{k.prima}», ma il sito è <strong>{k.dominio}</strong>: conta la parte finale del
          nome, non quella iniziale.
        </Avviso>
      )
    case 'ip':
      return (
        <Avviso tipo="attenzione">
          Al posto del nome del sito c’è un indirizzo numerico ({k.host}): i siti affidabili usano un nome.
        </Avviso>
      )
    case 'punycode':
      return (
        <Avviso tipo="attenzione">
          Il nome {k.host} contiene lettere accentate o di altri alfabeti, scritte nelle parti che iniziano con
          «xn--»: può imitare il nome di un sito noto con lettere che sembrano uguali.
        </Avviso>
      )
    case 'accorciato':
      return (
        <Avviso tipo="attenzione">
          È un indirizzo accorciato ({k.servizio}): dove porta davvero si scopre solo aprendolo.
        </Avviso>
      )
    case 'reindirizzamento':
      return (
        <Avviso tipo="attenzione">
          Il collegamento passa da {k.servizio} e porta a <strong>{k.dominio}</strong>:
          <span className="collegamento-url">{k.destinazione}</span>
        </Avviso>
      )
    case 'parametro':
      return (
        <Avviso tipo="attenzione">
          Dentro il collegamento c’è un altro indirizzo, verso <strong>{k.dominio}</strong>: il sito può rimandarti
          lì, oppure usarlo per sembrare affidabile.
          <span className="collegamento-url">{k.destinazione}</span>
        </Avviso>
      )
    case 'http':
      return (
        <Avviso tipo="attenzione">
          Il collegamento a {k.host} non è protetto (http, senza la s): quello che scrivi nella pagina viaggia in
          chiaro.
        </Avviso>
      )
  }
}
